import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getCampaigns, createCampaign, updateCampaign, setCampaignActive, deleteCampaign,
  getAllPlans,
} from '../../services/api';
import { useConfirm } from '../../components/ConfirmDialog';
import Toggle from '../../components/Toggle';
import MoneyInput from '../../components/MoneyInput';
import { formatDate, formatDateTime } from '../../utils/formatDateTime';

/**
 * Gestão de campanhas de desconto.
 *
 * Modelo:
 *   - Uma campanha = nome + janela de datas (starts_at/ends_at)
 *   - Cada campanha tem uma lista de (plano_default, preço_promocional)
 *   - Apenas UMA campanha pode estar ativa por vez — ao ativar uma,
 *     o backend desativa as outras automaticamente
 *   - A landing pública (atendoapp.com) consulta a campanha ativa
 *     dentro da janela temporal e mostra preço riscado + "válida até..."
 */

const fmtDate = formatDate;
const fmtDateTime = formatDateTime;

const fmtMoney = n => `R$ ${parseFloat(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

// Converte Date → string "YYYY-MM-DDTHH:MM" aceita por <input type="datetime-local">
function toLocalInput(date) {
  if (!date) return '';
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function isInWindow(campaign) {
  if (!campaign.active) return false;
  const now = new Date();
  return new Date(campaign.starts_at) <= now && now <= new Date(campaign.ends_at);
}

function isFuture(campaign) {
  return new Date(campaign.starts_at) > new Date();
}

function isExpired(campaign) {
  return new Date(campaign.ends_at) < new Date();
}

export default function MasterCampaigns() {
  const confirm = useConfirm();

  const [campaigns, setCampaigns] = useState([]);
  const [defaultPlans, setDefaultPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modal, setModal] = useState(null); // null | 'new' | campaignObject
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState({});

  function emptyForm() {
    return {
      name: '',
      starts_at: '',
      ends_at: '',
      active: false,
      // NULL (string vazia) = desconto perdura enquanto a janela da campanha
      // estiver no ar. Valor numérico = trava o preço promocional pelos N
      // primeiros meses de adesão do tenant (ex: "50% off nos 3 primeiros meses").
      duration_months: '',
      // Map plan_id → { enabled: bool, promotional_price: string }
      planPrices: {},
    };
  }

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getCampaigns(), getAllPlans()])
      .then(([c, p]) => {
        setCampaigns(c.data.campaigns || []);
        const defaults = (p.data.plans || []).filter(x => x.is_default && x.active);
        defaults.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        setDefaultPlans(defaults);
      })
      .catch(() => toast.error('Erro ao carregar campanhas'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const f = emptyForm();
    // pré-popula o mapa de planos default com enabled=false
    f.planPrices = Object.fromEntries(
      defaultPlans.map(p => [p.id, { enabled: false, promotional_price: '' }])
    );
    setForm(f);
    setModal('new');
  };

  const openEdit = (c) => {
    const priceMap = Object.fromEntries(
      c.plan_prices.map(pp => [pp.plan_id, { enabled: true, promotional_price: String(pp.promotional_price) }])
    );
    // Preenche os planos default que não estão na campanha com enabled=false
    for (const p of defaultPlans) {
      if (!priceMap[p.id]) {
        priceMap[p.id] = { enabled: false, promotional_price: '' };
      }
    }
    setForm({
      name: c.name,
      starts_at: toLocalInput(c.starts_at),
      ends_at: toLocalInput(c.ends_at),
      active: Boolean(c.active),
      duration_months: c.duration_months != null ? String(c.duration_months) : '',
      planPrices: priceMap,
    });
    setModal(c);
  };

  const closeModal = () => {
    if (saving) return;
    setModal(null);
  };

  const togglePlanEnabled = (planId) => {
    setForm(f => ({
      ...f,
      planPrices: {
        ...f.planPrices,
        [planId]: {
          ...f.planPrices[planId],
          enabled: !f.planPrices[planId]?.enabled,
        },
      },
    }));
  };

  const updatePlanPrice = (planId, value) => {
    setForm(f => ({
      ...f,
      planPrices: {
        ...f.planPrices,
        [planId]: {
          ...f.planPrices[planId],
          promotional_price: value,
        },
      },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || form.name.trim().length < 2) {
      return toast.error('Informe um nome válido para a campanha');
    }
    if (!form.starts_at || !form.ends_at) {
      return toast.error('Informe data de início e término');
    }
    const startsAt = new Date(form.starts_at);
    const endsAt = new Date(form.ends_at);
    if (endsAt <= startsAt) {
      return toast.error('A data de término deve ser posterior ao início');
    }

    // Monta array de plan_prices só com os que estão enabled
    const planPrices = [];
    for (const [planIdStr, entry] of Object.entries(form.planPrices)) {
      if (!entry.enabled) continue;
      const price = parseFloat(entry.promotional_price);
      if (Number.isNaN(price) || price < 0) {
        const plan = defaultPlans.find(p => p.id === parseInt(planIdStr));
        return toast.error(`Preço inválido para ${plan?.name || 'plano'}`);
      }
      const plan = defaultPlans.find(p => p.id === parseInt(planIdStr));
      if (plan && price >= parseFloat(plan.price)) {
        return toast.error(`O preço promocional de ${plan.name} deve ser MENOR que o preço original (${fmtMoney(plan.price)})`);
      }
      planPrices.push({
        plan_id: parseInt(planIdStr),
        promotional_price: price,
      });
    }

    if (form.active && planPrices.length === 0) {
      return toast.error('Uma campanha ativa precisa ter pelo menos 1 plano com desconto.');
    }

    setSaving(true);
    try {
      // duration_months: input vazio = NULL (enquanto durar janela);
      // valor numérico = travar preço pelos N primeiros meses de adesão.
      const durationParsed = form.duration_months.trim() === ''
        ? null
        : parseInt(form.duration_months, 10);
      if (durationParsed !== null && (isNaN(durationParsed) || durationParsed < 1 || durationParsed > 120)) {
        setSaving(false);
        return toast.error('Duração deve ser entre 1 e 120 meses (ou vazio pra enquanto durar a janela).');
      }

      const payload = {
        name: form.name.trim(),
        starts_at: startsAt.toISOString(),
        ends_at: endsAt.toISOString(),
        active: form.active,
        duration_months: durationParsed,
        plan_prices: planPrices,
      };
      if (modal === 'new') {
        await createCampaign(payload);
        toast.success('Campanha criada!');
      } else {
        await updateCampaign(modal.id, payload);
        toast.success('Campanha atualizada!');
      }
      setModal(null);
      load();
    } catch (err) {
      // Diagnóstico completo: se a resposta é HTML ou não-JSON, mostra o
      // status code pra ajudar a identificar o problema no servidor.
      const data = err.response?.data;
      const status = err.response?.status;
      const errorMsg = typeof data === 'object' && data?.error
        ? data.error
        : typeof data === 'string'
          ? `Erro ${status || '?'}: resposta inesperada do servidor`
          : err.message
            ? `Erro ${status || 'rede'}: ${err.message}`
            : 'Erro ao salvar campanha';
      console.error('[CAMPAIGN SAVE]', { status, dataType: typeof data, data: typeof data === 'string' ? data.slice(0, 200) : data, message: err.message });
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (c) => {
    const next = !c.active;
    const ok = await confirm({
      title: next ? `Ativar "${c.name}"?` : `Desativar "${c.name}"?`,
      message: next
        ? 'Esta campanha passará a ser exibida na landing pública dentro da janela de datas. Se houver outra campanha ativa, ela será desativada automaticamente (só 1 ativa por vez).'
        : 'A campanha deixará de ser exibida na landing. Os preços promocionais ficam salvos — você pode reativar depois.',
      confirmText: next ? 'Ativar' : 'Desativar',
      variant: next ? 'success' : 'warning',
    });
    if (!ok) return;

    setActing(p => ({ ...p, [c.id]: true }));
    try {
      await setCampaignActive(c.id, next);
      toast.success(next ? 'Campanha ativada' : 'Campanha desativada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao alterar status');
    } finally {
      setActing(p => ({ ...p, [c.id]: false }));
    }
  };

  const handleDelete = async (c) => {
    const ok = await confirm({
      title: `Excluir "${c.name}"?`,
      message: 'A campanha e todos os preços promocionais dela serão removidos permanentemente. Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteCampaign(c.id);
      toast.success('Campanha excluída');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  const statusLabel = (c) => {
    if (isInWindow(c)) return { label: '🟢 No ar', color: '#16a34a', bg: '#dcfce7' };
    if (c.active && isFuture(c)) return { label: '⏳ Agendada', color: '#f59e0b', bg: '#fef3c7' };
    if (c.active && isExpired(c)) return { label: '⚠️ Ativa mas expirada', color: '#dc2626', bg: '#fee2e2' };
    if (isExpired(c)) return { label: '🔒 Expirada', color: '#64748b', bg: '#f1f5f9' };
    return { label: '💤 Inativa', color: '#64748b', bg: '#f1f5f9' };
  };

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Campanhas de desconto</h2>
          <p>Campanhas aplicadas aos planos default na landing institucional.</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openNew} disabled={defaultPlans.length === 0}>
            + Nova campanha
          </button>
        </div>
      </div>

      <div className="info-box" style={{ fontSize: 12, marginBottom: 16 }}>
        💡 <strong>Regra:</strong> apenas <strong>uma campanha ativa</strong> por vez. Ao ativar uma, as demais são
        desativadas automaticamente. A campanha só aparece na landing pública (<code>atendoapp.com</code>) dentro da
        janela de datas. Campanhas futuras ficam "agendadas" até o horário de início.
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
      ) : campaigns.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔥</div>
          <h3>Nenhuma campanha cadastrada</h3>
          <p>Clique em "+ Nova campanha" para criar a primeira.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {campaigns.map(c => {
            const status = statusLabel(c);
            const inWindow = isInWindow(c);
            return (
              <div
                key={c.id}
                className="mobile-card"
                style={{
                  background: 'var(--white)',
                  borderRadius: 12,
                  border: inWindow ? '2px solid #16a34a' : '1px solid var(--gray-200)',
                  borderLeft: `4px solid ${status.color}`,
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--gray-900)' }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                      {fmtDateTime(c.starts_at)} → {fmtDateTime(c.ends_at)}
                    </div>
                  </div>
                  <span style={{
                    background: status.bg,
                    color: status.color,
                    padding: '4px 10px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 700,
                  }}>{status.label}</span>
                </div>

                {/* Planos com desconto nessa campanha */}
                {c.plan_prices && c.plan_prices.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: 8,
                  }}>
                    {c.plan_prices.map(pp => (
                      <div key={pp.plan_id} style={{
                        background: 'var(--gray-50)',
                        border: '1px solid var(--gray-200)',
                        borderRadius: 8,
                        padding: '8px 12px',
                      }}>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>
                          {pp.plan_name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          <s style={{ fontSize: 11, color: 'var(--gray-400)' }}>{fmtMoney(pp.original_price)}</s>
                          <strong style={{ fontSize: 14, color: '#16a34a' }}>{fmtMoney(pp.promotional_price)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', fontStyle: 'italic' }}>
                    Sem planos com desconto configurados.
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6, borderTop: '1px solid var(--gray-100)' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)}>Editar</button>
                  <button
                    className={`btn btn-sm ${c.active ? 'btn-warning' : 'btn-success'}${acting[c.id] ? ' btn-loading' : ''}`}
                    onClick={() => handleToggleActive(c)}
                    disabled={acting[c.id]}
                  >
                    {acting[c.id] ? '' : c.active ? 'Desativar' : 'Ativar'}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c)}>
                    Excluir
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal criar/editar */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <h3>{modal === 'new' ? 'Nova campanha' : `Editar: ${modal.name}`}</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body" style={{ maxHeight: 560, overflowY: 'auto' }}>
                <div className="form-group">
                  <label>Nome da campanha *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ex.: Black Friday 2026, Lançamento, Promoção de Verão"
                    maxLength={100}
                    autoFocus
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Início *</label>
                    <input
                      type="datetime-local"
                      value={form.starts_at}
                      onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Término *</label>
                    <input
                      type="datetime-local"
                      value={form.ends_at}
                      onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Duração do desconto (meses após adesão)</label>
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={form.duration_months}
                    onChange={e => setForm(f => ({ ...f, duration_months: e.target.value }))}
                    placeholder="Deixe vazio para manter enquanto a janela estiver no ar"
                    style={{ maxWidth: 320 }}
                  />
                  <small>
                    Vazio: o desconto vale pra quem assinar a qualquer momento dentro da janela acima, sem limite de duração por assinante.
                    <br />
                    Valor (ex.: 3): ao assinar durante a campanha, o preço promocional fica travado pelos primeiros <strong>N meses</strong> de cada tenant e depois volta automaticamente ao preço cheio do plano.
                  </small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Toggle
                      checked={form.active}
                      onChange={v => setForm(f => ({ ...f, active: v }))}
                    />
                    <span style={{ fontWeight: 600 }}>Ativar esta campanha</span>
                  </label>
                  <small>
                    {form.active
                      ? 'Ao salvar, esta será a campanha ativa (e outras serão desativadas automaticamente).'
                      : 'A campanha fica salva como rascunho/inativa.'}
                  </small>
                </div>

                <h4 style={{ margin: '20px 0 8px', fontSize: 14 }}>Preços promocionais por plano</h4>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 10 }}>
                  Marque os planos que terão desconto nesta campanha e informe o preço promocional (deve ser <strong>menor</strong> que o original).
                </div>

                {defaultPlans.length === 0 ? (
                  <div className="empty-state" style={{ padding: 20 }}>
                    <p>Nenhum plano default encontrado. Rode a sync de planos primeiro.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {defaultPlans.map(p => {
                      const entry = form.planPrices[p.id] || { enabled: false, promotional_price: '' };
                      return (
                        <div
                          key={p.id}
                          style={{
                            border: `1.5px solid ${entry.enabled ? 'var(--primary)' : 'var(--gray-200)'}`,
                            borderRadius: 8,
                            padding: '10px 12px',
                            background: entry.enabled ? 'rgba(37,99,235,0.04)' : 'transparent',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Toggle
                              checked={entry.enabled}
                              onChange={() => togglePlanEnabled(p.id)}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                                Preço original: {fmtMoney(p.price)}
                              </div>
                            </div>
                            {entry.enabled && (
                              <MoneyInput
                                value={entry.promotional_price}
                                onChange={v => updatePlanPrice(p.id, v)}
                                min={0}
                                placeholder="R$ 0,00"
                                style={{ width: 140 }}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>
                  {saving ? 'Salvando...' : (modal === 'new' ? 'Criar campanha' : 'Salvar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
